import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { COLORS, BRAND } from '@/lib/brand';
import {
  LayoutDashboard,
  MapPin,
  Users,
  Package,
  Warehouse,
  ShoppingCart,
  FileSpreadsheet,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeft,
  Truck,
  FileText,
  DollarSign,
  Building2,
  ClipboardList,
  ClipboardCheck,
  SlidersHorizontal,
  Send,
  BarChart3
} from 'lucide-react';
import { motion } from 'framer-motion';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/locations', icon: MapPin, label: 'Locales' },
    { path: '/admin/users', icon: Users, label: 'Usuarios' },
    { path: '/admin/items', icon: Package, label: 'Productos' },
    { path: '/admin/suppliers', icon: Building2, label: 'Proveedores' },
    { path: '/admin/inventory', icon: Warehouse, label: 'Inventario' },
    { path: '/admin/local-inventories', icon: ClipboardList, label: 'Conteos' },
    { path: '/admin/recuentos', icon: ClipboardCheck, label: 'Recuentos' },
    { path: '/admin/justo', icon: FileSpreadsheet, label: 'Despachos Justo' },
    { path: '/admin/receptions', icon: FileText, label: 'Recepciones' },
    { path: '/admin/prices', icon: DollarSign, label: 'Precios' },
    { path: '/admin/orders', icon: ShoppingCart, label: 'Pedidos' },
    { path: '/admin/consolidated', icon: TrendingUp, label: 'Consolidado' },
    { path: '/admin/logistics', icon: Truck, label: 'Logística' },
    { path: '/admin/shipments', icon: Send, label: 'Envíos' },
    { path: '/admin/reports', icon: BarChart3, label: 'Reportes' },
    { path: '/admin/import', icon: FileSpreadsheet, label: 'Importar Excel' },
    { path: '/admin/ajustes', icon: SlidersHorizontal, label: 'Ajustes' },
    { path: '/admin/settings', icon: Settings, label: 'Configuración' },
  ];

  const sidebarVariants = {
    expanded: { width: 256 },
    collapsed: { width: 72 }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: COLORS.background.DEFAULT }}>
      {/* Overlay para móvil */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar Desktop - Naranja TUPU Stock */}
      <motion.aside
        initial={false}
        animate={sidebarCollapsed ? 'collapsed' : 'expanded'}
        variants={sidebarVariants}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="hidden lg:flex flex-col h-screen sticky top-0 z-30 overflow-hidden shadow-lg"
        style={{ 
          backgroundColor: COLORS.sidebar.bg,
          borderRight: 'none'
        }}
        data-testid="admin-sidebar"
      >
        <div 
          className={`p-4 flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}
          style={{ 
            borderBottom: `1px solid ${COLORS.sidebar.border}`
          }}
        >
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <div 
                className="h-10 w-10 rounded-lg flex items-center justify-center shadow-sm"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <Package className="w-6 h-6" style={{ color: COLORS.sidebar.bg }} />
              </div>
              <div>
                <h1 className="text-lg font-bold" style={{ color: COLORS.sidebar.text }}>{BRAND.name}</h1>
                <p className="text-xs truncate opacity-80" style={{ color: COLORS.sidebar.text }}>{user?.name}</p>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <div 
              className="h-10 w-10 rounded-lg flex items-center justify-center shadow-sm"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <Package className="w-6 h-6" style={{ color: COLORS.sidebar.bg }} />
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`p-2 rounded-md transition-colors ${sidebarCollapsed ? 'hidden' : ''}`}
            style={{ color: COLORS.sidebar.icon }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.sidebar.hoverBg}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            data-testid="toggle-sidebar-button"
            title={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            {sidebarCollapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact 
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  sidebarCollapsed ? 'justify-center' : ''
                }`}
                style={{
                  backgroundColor: isActive ? COLORS.sidebar.activeBg : 'transparent',
                  color: isActive ? COLORS.sidebar.activeText : COLORS.sidebar.text
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = COLORS.sidebar.hoverBg;
                    e.currentTarget.style.color = COLORS.sidebar.hoverText;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = COLORS.sidebar.text;
                  }
                }}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" style={{ color: isActive ? COLORS.sidebar.activeIcon : COLORS.sidebar.icon }} />
                {!sidebarCollapsed && <span className="font-medium truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-2" style={{ borderTop: `1px solid ${COLORS.sidebar.border}` }}>
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2.5 w-full rounded-lg transition-all ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
            style={{ color: COLORS.sidebar.text }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
              e.currentTarget.style.color = '#FFFFFF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = COLORS.sidebar.text;
            }}
            data-testid="admin-logout-button"
            title={sidebarCollapsed ? 'Cerrar sesión' : undefined}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="font-medium">Cerrar sesión</span>}
          </button>
        </div>
      </motion.aside>

      {/* Sidebar Móvil - Naranja TUPU Stock */}
      <motion.aside
        initial={{ x: -280 }}
        animate={{ x: mobileMenuOpen ? 0 : -280 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="lg:hidden fixed left-0 top-0 w-64 flex flex-col h-full z-50 shadow-xl"
        style={{ 
          backgroundColor: COLORS.sidebar.bg,
          borderRight: 'none'
        }}
        data-testid="admin-sidebar-mobile"
      >
        <div className="p-4" style={{ borderBottom: `1px solid ${COLORS.sidebar.border}` }}>
          <div className="flex items-center gap-3">
            <div 
              className="h-10 w-10 rounded-lg flex items-center justify-center shadow-sm"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <Package className="w-6 h-6" style={{ color: COLORS.sidebar.bg }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: COLORS.sidebar.text }}>{BRAND.name}</h1>
              <p className="text-xs mt-0.5 opacity-80" style={{ color: COLORS.sidebar.text }}>{user?.name}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact 
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all"
                style={{
                  backgroundColor: isActive ? COLORS.sidebar.activeBg : 'transparent',
                  color: isActive ? COLORS.sidebar.activeText : COLORS.sidebar.text
                }}
              >
                <Icon className="w-5 h-5" style={{ color: isActive ? COLORS.sidebar.activeIcon : COLORS.sidebar.icon }} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-2" style={{ borderTop: `1px solid ${COLORS.sidebar.border}` }}>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg transition-all"
            style={{ color: COLORS.sidebar.text }}
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Cerrar sesión</span>
          </button>
        </div>
      </motion.aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white px-4 lg:px-6 py-3 flex items-center justify-between sticky top-0 z-20" style={{ borderBottom: `1px solid ${COLORS.border.DEFAULT}` }}>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-md transition-colors lg:hidden"
            style={{ color: COLORS.sidebar.text }}
            onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.sidebar.hoverBg}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-4 ml-auto">
            <div className="text-right">
              <p className="text-sm font-medium" style={{ color: COLORS.text.primary }}>{user?.name}</p>
              <p className="text-xs" style={{ color: COLORS.text.secondary }}>Administrador</p>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-y-auto" style={{ backgroundColor: COLORS.background.DEFAULT }} data-testid="admin-main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;